import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MOVEMENT_TYPES, PURPOSES, GP_STATUS_LABEL, GP_STATUS_COLOR } from "@/config/gatepass";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { ExportButton } from "@/components/ExportDialog";

export default function GatePassRegister({ embedded = false, onNew }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ movement_type: "all", purpose: "all", status: "all" });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(() => {
    if (!can("gate_pass", "view")) { setLoading(false); return; }
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/gate-passes", { params })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [q, filters, dateFrom, dateTo]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const downloadPdf = async (row) => {
    try {
      const res = await api.get(`/gate-passes/${row.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(row.gate_pass_number || "gate_pass_draft").replace(/\//g, "_")}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate PDF"); }
  };
  const printPdf = async (row) => {
    try {
      const res = await api.get(`/gate-passes/${row.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { toast.error("Could not open PDF"); }
  };
  if (!can("gate_pass", "view"))
    return (
      <div className="max-w-md mx-auto mt-16 text-center" data-testid="restricted-notice">
        <div className="h-14 w-14 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center mx-auto mb-4"><Icons.Lock className="h-6 w-6" /></div>
        <h2 className="font-display text-xl font-bold">Access Restricted</h2>
        <p className="text-muted-foreground text-sm mt-2">You don't have permission to view the Gate Pass Register.</p>
      </div>
    );

  const purposeText = (row) => (row.purpose === "Other" ? row.purpose_other : row.purpose) || "—";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {embedded ? (
          <p className="text-sm text-muted-foreground">{items.length} record{items.length !== 1 ? "s" : ""} · material &amp; vehicle movements</p>
        ) : (
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#1e3a5f1a", color: "#1e3a5f" }}>
              <Icons.ClipboardCheck className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight">Gate Pass Register</h1>
              <p className="text-sm text-muted-foreground">{items.length} record{items.length !== 1 ? "s" : ""} · material &amp; vehicle movements</p>
            </div>
          </div>
        )}
        <div className="flex gap-2">
          <Button variant="outline" className="gap-1.5" onClick={() => navigate("/gate-pass/verify")} data-testid="gp-verify-nav-btn"><Icons.QrCode className="h-4 w-4" />Verify</Button>
          {can("gate_pass", "export") && <ExportButton report="gate_pass" title="Gate Pass Register" testid="gp-export-btn" filters={{ ...filters, q, date_from: dateFrom, date_to: dateTo }} />}
          {can("gate_pass", "create") && (
            <Button className="gap-1.5 active:scale-95 transition-transform" onClick={() => (onNew ? onNew() : navigate("/gate-pass/new"))} data-testid="gp-new-btn">
              <Icons.Plus className="h-4 w-4" />New Gate Pass
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search no., party, vehicle, driver, bill…" className="pl-9" data-testid="gp-search" />
        </div>
        <Select value={filters.movement_type} onValueChange={(v) => setFilters((p) => ({ ...p, movement_type: v }))}>
          <SelectTrigger className="w-[150px]" data-testid="gp-filter-movement"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Movement</SelectItem>{MOVEMENT_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.purpose} onValueChange={(v) => setFilters((p) => ({ ...p, purpose: v }))}>
          <SelectTrigger className="w-[160px]" data-testid="gp-filter-purpose"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Purpose</SelectItem>{PURPOSES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
          <SelectTrigger className="w-[190px]" data-testid="gp-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem>{Object.entries(GP_STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px]" data-testid="gp-filter-from" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px]" data-testid="gp-filter-to" />
      </div>

      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50 hover:bg-secondary/50">
              {["Gate Pass No.", "Date", "Time", "Movement", "Purpose", "Party", "Vehicle", "Driver", "Mobile", "Bill No.", "Status", "Issued By", ""].map((h, i) => <TableHead key={i} className="overline text-muted-foreground whitespace-nowrap">{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={13} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>}
            {!loading && items.length === 0 && <TableRow><TableCell colSpan={13} className="text-center py-12 text-muted-foreground">No gate passes yet.</TableCell></TableRow>}
            {!loading && items.map((row, i) => (
              <motion.tr key={row.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.2) }} className="border-b hover:bg-secondary/40">
                <TableCell className="font-medium whitespace-nowrap">
                  <button className="text-primary hover:underline" onClick={() => navigate(`/gate-pass/${row.id}`)} data-testid={`gp-row-${row.id}`}>{row.gate_pass_number || "— draft —"}</button>
                  {row.is_test && <Badge variant="secondary" className="ml-1.5 bg-amber-100 text-amber-800 text-[10px]" data-testid={`gp-test-${row.id}`}>TEST</Badge>}
                </TableCell>
                <TableCell className="whitespace-nowrap">{row.issue_date || "—"}</TableCell>
                <TableCell className="whitespace-nowrap">{row.issue_time || "—"}</TableCell>
                <TableCell>{row.movement_type || "—"}</TableCell>
                <TableCell className="max-w-[140px] truncate">{purposeText(row)}</TableCell>
                <TableCell className="font-medium">{row.party_name || "—"}</TableCell>
                <TableCell className="whitespace-nowrap">{row.vehicle_number || "—"}</TableCell>
                <TableCell>{row.driver_name || "—"}</TableCell>
                <TableCell className="whitespace-nowrap">{row.driver_mobile || "—"}</TableCell>
                <TableCell className="whitespace-nowrap" data-testid={`gp-bill-${row.id}`}>{row.bill_number || "—"}</TableCell>
                <TableCell><Badge variant="secondary" className={GP_STATUS_COLOR[row.status] || ""}>{GP_STATUS_LABEL[row.status] || row.status}</Badge></TableCell>
                <TableCell className="whitespace-nowrap">{row.authorized_by_name || row.created_by || "—"}</TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`gp-menu-${row.id}`}><Icons.MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/gate-pass/${row.id}`)}><Icons.Eye className="h-4 w-4 mr-2" />View</DropdownMenuItem>
                      {can("gate_pass", "download") && <DropdownMenuItem onClick={() => printPdf(row)} data-testid={`gp-print-${row.id}`}><Icons.Printer className="h-4 w-4 mr-2" />Print</DropdownMenuItem>}
                      {can("gate_pass", "download") && <DropdownMenuItem onClick={() => downloadPdf(row)} data-testid={`gp-pdf-${row.id}`}><Icons.FileDown className="h-4 w-4 mr-2" />Download PDF</DropdownMenuItem>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </motion.tr>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
